import {
  CUT_ROSE_SECTORS,
  EDGE_ALIGN_GOOD_DEG,
  EDGE_ALIGN_OK_DEG,
  EDGE_QUALITY,
  type Difficulty,
  type EdgeQuality,
  type Filo,
  type Inclinazione,
  type Lato,
  type Zone,
} from '../config/constants';
import type { StrokeClassification } from '../hit-detection/strokeClassify';
import type { DrillTarget } from './repEvaluator';

export type Shot = {
  tMs: number;
  zone: Zone;
  tipSpeedMPerSec: number;
  edgeAngleDeg: number;
  /**
   * Cut direction in the viewer-facing vertical plane: 0° = down (fendente), 90° = right
   * (tondo), 180° = up (montante); clockwise. `null`/absent for thrusts and for cuts whose
   * direction was too close to the viewing axis to be meaningful. Optional for back-compat
   * with sessions stored before cut-angle tracking existed.
   */
  cutAngleDeg?: number | null;
  /**
   * Classified colpo (lato × inclinazione × filo), each axis null when ambiguous.
   * Absent for thrusts and for sessions stored before classification existed.
   */
  stroke?: StrokeClassification | null;
  /**
   * Edge quality of the strike. A thrust (`'thrust'`) carries `edgeAngleDeg: 0`, which is NOT
   * a perfect cut — it must be excluded from the edge-alignment percentages. Optional for
   * back-compat with sessions stored before this field existed (those thrusts stay skewed).
   */
  quality?: EdgeQuality;
};

/** Sentinel bucket for an axis the classifier couldn't decide. */
export const STROKE_AMBIGUOUS = 'ambiguo' as const;
export type StrokeAxisKey<T extends string> = T | typeof STROKE_AMBIGUOUS;

/** Per-axis cut counts (the `ambiguo` bucket holds swings the classifier left null). */
export type StrokeBreakdown = {
  lato: Record<StrokeAxisKey<Lato>, number>;
  inclinazione: Record<StrokeAxisKey<Inclinazione>, number>;
  filo: Record<StrokeAxisKey<Filo>, number>;
  /** Number of classified cuts (excludes thrusts / shots with no stroke). */
  classified: number;
};

export type PellStats = {
  kind: 'pell';
  total: number;
  /** Thrusts (colpi di punta) among the strikes — excluded from the edge-% denominator. */
  thrusts: number;
  edgeGoodPct: number;
  edgeAcceptablePct: number;
  avgTipSpeed: number;
  zoneDistribution: Record<Zone, number>;
  /** Count of cuts per directional sector (length CUT_ROSE_SECTORS; index 0 = down, clockwise by 45°). */
  cutAngleHistogram: number[];
  /** Per-axis colpo counts (lato × inclinazione × filo + ambiguo). */
  strokeBreakdown: StrokeBreakdown;
};

export type TargetsStats = {
  kind: 'targets';
  total: number;
  hits: number;
  hitRatePct: number;
  avgReactionMs: number;
  avgTipSpeed: number;
  avgAccuracyM: number;
  /** Hit rate (%) per zone, 0 if no spawns in that zone. */
  hitRateByZone: Record<Zone, number>;
  /** Total spawns per zone (denominator for hitRateByZone). */
  spawnsByZone: Record<Zone, number>;
};

/** Why reps failed in a prescriptive drill (the dominant fault per rep). */
export type DrillReasonKey = 'zona' | 'lato' | 'inclinazione' | 'filo' | 'piattonata' | 'ambiguo';

export type DrillStats = {
  kind: 'drill';
  /** The colpo + zona the reps were scored against. */
  target: DrillTarget;
  correct: number;
  attempts: number;
  accuracyPct: number;
  elapsedMs: number;
  /** Average edge-alignment angle (deg) over the correct reps (lower = cleaner). */
  avgEdgeDeg: number;
  /** Count of failed reps per dominant fault. */
  reasonCounts: Record<DrillReasonKey, number>;
};

/** Fields shared by every arcade run, regardless of technique. */
type ArcadeStatsCommon = {
  kind: 'arcade';
  /**
   * Difficulty the run was played at (#43) — selects the best/leaderboard board and is shown in
   * the summary/history. `custom` runs carry it too (for display) but never persist a record.
   * Absent on sessions stored before #43; readers treat a missing value as `'medium'`.
   */
  difficulty: Difficulty;
  /** Final run score (sum of points over all resolved targets). */
  score: number;
  /** Longest consecutive clean-hit streak reached during the run. */
  maxCombo: number;
  /** Successful hits (clean + sloppy). */
  hits: number;
  /** Targets resolved (hit, wrong direction, or TTL timeout). */
  total: number;
  /** hits / total as a percentage. */
  accuracyPct: number;
};

/** A cut ("Sfida") run: hits scored by edge quality on a required direction. */
export type ArcadeCutStats = ArcadeStatsCommon & {
  technique: 'cut';
  /** Average edge-alignment angle (deg) over the hits (lower = cleaner). */
  avgEdgeDeg: number;
};

/** A thrust (punte) run: hits scored by centering, with the diagnostics inherited from #44. */
export type ArcadeThrustStats = ArcadeStatsCommon & {
  technique: 'thrust';
  /** Average distance from the disc center over the hits (m; lower = better centered). */
  avgAccuracyM: number;
  /** Average spawn→hit reaction over the hits (ms). */
  avgReactionMs: number;
  /** Hit rate (%) per zone, 0 if no spawns in that zone. */
  hitRateByZone: Record<Zone, number>;
  /** Total spawns per zone (denominator for hitRateByZone). */
  spawnsByZone: Record<Zone, number>;
};

/**
 * Discriminated on `technique`. Sessions persisted before #44 carry no `technique` and the
 * cut-only `avgEdgeDeg`; readers treat a missing technique as `'cut'` for back-compat.
 */
export type ArcadeStats = ArcadeCutStats | ArcadeThrustStats;

/** Discriminated union — switch on `kind` to render or aggregate. */
export type SessionStats = PellStats | TargetsStats | DrillStats | ArcadeStats;

export function emptyStrokeBreakdown(): StrokeBreakdown {
  return {
    lato: { mandritto: 0, riverso: 0, ambiguo: 0 },
    inclinazione: { fendente: 0, sgualembro: 0, tondo: 0, ridoppio: 0, montante: 0, ambiguo: 0 },
    filo: { dritto: 0, falso: 0, ambiguo: 0 },
    classified: 0,
  };
}

export function emptyPellStats(): PellStats {
  return {
    kind: 'pell',
    total: 0,
    thrusts: 0,
    edgeGoodPct: 0,
    edgeAcceptablePct: 0,
    avgTipSpeed: 0,
    zoneDistribution: { head: 0, shoulder: 0, chest: 0, flank: 0, leg: 0 },
    cutAngleHistogram: new Array<number>(CUT_ROSE_SECTORS).fill(0),
    strokeBreakdown: emptyStrokeBreakdown(),
  };
}

/** Map a cut angle (deg, 0 = down, clockwise) to its rose sector index [0, CUT_ROSE_SECTORS). */
export function cutAngleSector(deg: number): number {
  const sectorDeg = 360 / CUT_ROSE_SECTORS;
  return ((Math.round(deg / sectorDeg) % CUT_ROSE_SECTORS) + CUT_ROSE_SECTORS) % CUT_ROSE_SECTORS;
}

/** Compat alias — keep until call sites migrate. */
export const emptyStats = emptyPellStats;

export function aggregate(shots: readonly Shot[]): PellStats {
  const total = shots.length;
  if (total === 0) return emptyPellStats();

  let goodCount = 0;
  let okCount = 0;
  let thrusts = 0;
  let speedSum = 0;
  const zones: Record<Zone, number> = { head: 0, shoulder: 0, chest: 0, flank: 0, leg: 0 };
  const cutAngleHistogram = new Array(CUT_ROSE_SECTORS).fill(0) as number[];
  const strokeBreakdown = emptyStrokeBreakdown();

  for (const s of shots) {
    // A thrust has edgeAngleDeg 0 but is not a cut — it must not count toward edge alignment.
    if (s.quality === EDGE_QUALITY.thrust) {
      thrusts++;
    } else {
      if (s.edgeAngleDeg <= EDGE_ALIGN_GOOD_DEG) goodCount++;
      if (s.edgeAngleDeg <= EDGE_ALIGN_OK_DEG) okCount++;
    }
    speedSum += s.tipSpeedMPerSec;
    zones[s.zone]++;
    if (s.cutAngleDeg != null && isFinite(s.cutAngleDeg)) {
      cutAngleHistogram[cutAngleSector(s.cutAngleDeg)]!++;
    }
    if (s.stroke) {
      strokeBreakdown.classified++;
      strokeBreakdown.lato[s.stroke.lato ?? STROKE_AMBIGUOUS]++;
      strokeBreakdown.inclinazione[s.stroke.inclinazione ?? STROKE_AMBIGUOUS]++;
      strokeBreakdown.filo[s.stroke.filo ?? STROKE_AMBIGUOUS]++;
    }
  }

  // Edge alignment is a cuts-only metric; thrusts are excluded from the denominator.
  const cutCount = total - thrusts;
  return {
    kind: 'pell',
    total,
    thrusts,
    edgeGoodPct: cutCount > 0 ? (goodCount / cutCount) * 100 : 0,
    edgeAcceptablePct: cutCount > 0 ? (okCount / cutCount) * 100 : 0,
    avgTipSpeed: speedSum / total,
    zoneDistribution: zones,
    cutAngleHistogram,
    strokeBreakdown,
  };
}
